import { ArrowBackIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Collapse,
  FormControl,
  FormErrorMessage,
  Img,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Field, Formik } from "formik";
// import {getYouTubeID} from "get-youtube-id";
import { observer } from "mobx-react-lite";
import React from "react";
import { object, string } from "yup";
import ExpandingTextArea from "../../../components/form-elements/ExpandingTextArea";
import {
  OverlayRouter,
  OverlayWrapper,
} from "../../../components/OverlayRouter";
import { useLocalStorage } from "../../../tutor-application/utils";
import {
  HideAndShowWrapper,
  ModalWrapper,
} from "../../../tutor-revamp/ArchiveSubjectEditForm";
import { SingleFormButton } from "../../../tutor-revamp/forms/FormButtons";
import { displayToast } from "../../../tutor-revamp/forms/SharedComponents";
import SpecialNeeds from "../../../tutor-revamp/SpecialNeeds";
import { MobileMenuItem } from "../../../tutor-revamp/SubjectEditForm";
import {
  createValidationSchema,
  generateFormComponent,
} from "../../../tutor-revamp/TeachingPreference";
import { scrollToId } from "../../../utils/functions";
import { TUTOR_PROFILE_PREFERENCES } from "./tutor_preferences";
import { useStreamingProfileDescription } from "../../../components/form-elements/ExpandingTextArea/hook";

const PROFILE_STEPS = {
  PROFILE_DESCRIPTION: "Create your profile summary",
  CATEGORY_SELECTION: "category_selection",
  SPECIAL_NEEDS: "Enter special needs experience",
  ACADEMICS: "Academics",
  TEST_PREP: "Test Prep",
  MUSIC: "Music",
  LANGUAGES: "Languages",
};

const AcademicPreference: React.FC<{
  fields: any[];
  loading?: boolean;
  values?: any;
  disabledOptions?: string[];
  onBackClick: () => any;
  onSubmit: (vals: any) => any;
}> = ({
  fields = [],
  disabledOptions = [],
  loading,
  values = {},
  onSubmit,
  onBackClick,
}) => {
  const [showFormError, setShowFormError] = React.useState(false);
  const onSubmitClick = (errors = {}, values = {}) => {
    if (errors) {
      if (Object.values(errors).length || Object.values(values).length === 0) {
        setShowFormError(true);
        scrollToId("form-header-section");
      } else {
        setShowFormError(false);
      }
    }
  };
  const validationSchema =
    fields.length > 0 ? createValidationSchema(fields) : object().shape({});
  return (
    <Box as="section">
      <Button
        mb={{ base: 8, md: 10 }}
        onClick={onBackClick}
        leftIcon={<ArrowBackIcon />}
        variant="link"
        colorScheme="blue"
      >
        Back
      </Button>
      <VStack justifyContent="stretch" alignItems="stretch" spacing={4}>
        <Formik
          validationSchema={validationSchema}
          initialValues={values}
          onSubmit={(vals) => {
            onSubmit(vals);
          }}
        >
          {({ handleSubmit, errors, values, touched }) => {
            return (
              <form
                onSubmit={(value) => {
                  handleSubmit(value);
                  onSubmitClick(errors, values);
                }}
              >
                <Stack id="form-header-section" pb={12}>
                  <Stack pb={5}>
                    <Text
                      fontSize="3xl"
                      lineHeight="shorter"
                      fontWeight="bold"
                      as="h1"
                    >
                      Add category preferences
                    </Text>
                    <Text mt={1} color="gray.700">
                      Help clients understand your tutoring background and
                      preferences
                    </Text>

                    {showFormError && (
                      <Box pt={2}>
                        <Alert rounded="lg" status="error">
                          <AlertIcon />
                          Error submitting forms, some fields are required
                        </Alert>
                      </Box>
                    )}
                  </Stack>

                  <Stack spacing={8}>
                    {fields.map((fieldItem, index) => {
                      return (
                        <Box key={index}>
                          <Field
                            name={fieldItem.name}
                            render={({ field, form }: any) => {
                              let display = true;
                              const FormComponent =
                                generateFormComponent(fieldItem);
                              let props: any = {
                                formField: fieldItem,
                                value: field.value,
                                field: field,
                                form: form,
                                hasError:
                                  errors[field.name] &&
                                  form.dirty &&
                                  touched[field.name],
                                touched: touched[field.name],
                                errorMessage: errors[field.name],
                                disabledOptions,
                              };
                              const formValue =
                                fieldItem.type === "accordion-multi"
                                  ? values[fieldItem.accordionFieldName]
                                  : field.value;
                              if (fieldItem.type === "accordion-multi") {
                              }
                              let rendered = display ? (
                                <FormComponent
                                  onChange={(value: any) => {
                                    form.setFieldValue(field.name, value);
                                  }}
                                  {...props}
                                  value={formValue}
                                />
                              ) : null;
                              return (
                                <Box className="render-component">
                                  {rendered}
                                </Box>
                              );
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Stack>

                <SingleFormButton
                  nextText="Save my preference"
                  isLoading={loading}
                  onNextClick={handleSubmit}
                />
              </form>
            );
          }}
        </Formik>
      </VStack>
    </Box>
  );
};

const initialValue = {
  tutor_description: "",
  trackRecords: "",
};

const errorMessages = {
  tutor_description: "Oops! Too long. Try to keep it under 1500 characters",
  trackrecord: "Too long. Try make it 250 characters",
};

const placeholders: any = {
  tutor_description: {
    placeholder:
      "Let clients know why you're the best for them. You may start with a professional introduction or a description of your impact and competence as a tutor",
    characterLimit: 1500,
    height: "regular",
  },
  trackRecords: {
    label: "Your track record",
    tutor_description:
      "Give a quick summary of the most outstanding results you've achieved with previous students if any. Feel free to brag!",
    placeholder:
      "E.g. I had a First-Class, 4.87 CGPA from the University of Lagos as the Best Graduating Student in the Engineering Faculty in 2013",
    characterLimit: 250,
    height: "shorter",
  },
};

function getMaxLength(name: any) {
  return placeholders[name].characterLimit;
}

const validationSchema = object().shape({
  tutor_description: string()
    .transform((value) => value.replace(/ +/g, " "))
    .trim()
    .max(getMaxLength("tutor_description"), errorMessages["tutor_description"])
    .min(300, "Must be 300 characters at least")
    .required("Describe your expertise"),
  trackRecords: string()
    .transform((value) => value.replace(/ +/g, " "))
    .min(50, "Must be 50 characters at least")
    .max(250, errorMessages["trackrecord"])
    .trim("No unnecessary white spaces between text")
    .required("Enter your proudest achievement"),
});

const commonProps = ({ name }: any, { touched, errors }: any) => ({
  ...placeholders[name],
  isInvalid: Boolean(touched[name] && errors[name]),
  errorMessage: errors[name],
});
const ProfileDescriptionForm: React.FC<{
  value: any;
  onSubmit: (values: any) => Promise<any>;
  loading: boolean;
  onBackClick: () => void;
  generateProfileSummary?: () => any;
}> = ({ value, onSubmit, loading, onBackClick, generateProfileSummary }) => {
  const streamingCall = useStreamingProfileDescription(generateProfileSummary);
  const tutor_description_storage = useLocalStorage(
    "tutor_description",
    initialValue?.tutor_description
  );
  const trackRecords_storage = useLocalStorage(
    "trackRecords",
    initialValue?.trackRecords
  );
  const [errorObj, setErrorObjects] = React.useState({});
  const _handleSubmit = async (values: any, formikHelpers: any) => {
    formikHelpers.setSubmitting(true);
    try {
      let res = await onSubmit(values);
      tutor_description_storage.removeValue();
      trackRecords_storage.removeValue();
      formikHelpers.setSubmitting(false);
      return res;
    } catch (error: any) {
      formikHelpers.setSubmitting(false);
      if (error?.spellCheck) {
        formikHelpers.setErrors(error.spellCheck);
        if (error?.errors) {
          setErrorObjects(error.errors);
        }
      }
    }
  };

  const sampleDescription = useDisclosure();

  const defaultValues = {
    ...initialValue,
    ...value,
  };
  return (
    <div>
      <Button
        ml={-1}
        mb={{ base: 8, md: 10 }}
        size={useBreakpointValue({ base: "sm", md: "md" })}
        iconSpacing={1}
        colorScheme="blue"
        leftIcon={<ChevronLeftIcon fontSize={{ base: "xl", md: "2xl" }} />}
        variant="link"
        onClick={onBackClick}
      >
        Back
      </Button>
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          tutor_description: defaultValues.tutor_description
            ? defaultValues.tutor_description
            : tutor_description_storage.value ||
              defaultValues.tutor_description,
          trackRecords: defaultValues.trackRecords
            ? defaultValues.trackRecords
            : trackRecords_storage.value || defaultValues.trackRecords,
        }}
        enableReinitialize
        onSubmit={(values, formikAction) => _handleSubmit(values, formikAction)}
        render={({
          handleSubmit,
          values,
          setValues,
          errors,
          setFieldValue,
        }) => {
          return (
            <Box className="subject-description">
              <form onSubmit={handleSubmit}>
                <Stack pb={12}>
                  <Stack pb={5}>
                    <Text
                      fontSize="3xl"
                      lineHeight="shorter"
                      fontWeight="bold"
                      as="h1"
                    >
                      Tutor profile summary
                    </Text>
                    <Text mt={1} color="gray.700">
                      Introduce yourself to Tuteria community. Tell us about
                      your teaching experience, methodology, passion,
                      qualifications and anything else that makes you stand out.
                      Let clients know why they should hire you.
                    </Text>

                    <Stack pt={2}>
                      <HStack justifyContent="space-between">
                        <Button
                          variant="link"
                          type="button"
                          colorScheme="blue"
                          onClick={() => sampleDescription.onToggle()}
                        >
                          See some good examples
                        </Button>
                        {Boolean(generateProfileSummary) && (
                          <Button
                            type="button"
                            colorScheme="blue"
                            isLoading={streamingCall.loading}
                            onClick={() => {
                              streamingCall.generateDescription();
                            }}
                          >
                            Generate Profile Summary
                          </Button>
                        )}
                      </HStack>

                      <Collapse in={sampleDescription.isOpen}>
                        <Stack spacing={4}>
                          <Stack>
                            <Text fontWeight="medium">Example 1</Text>
                            <Box rounded="lg" overflow="hidden">
                              <Img
                                boxSize="full"
                                objectFit="cover"
                                src="https://res.cloudinary.com/tuteria/image/upload/v1673819043/landingpage/SampleDescription.png"
                              />
                            </Box>
                          </Stack>
                          <Stack>
                            <Text fontWeight="medium">Example 2</Text>
                            <Box rounded="lg" overflow="hidden">
                              <Img
                                boxSize="full"
                                objectFit="cover"
                                src="https://res.cloudinary.com/tuteria/image/upload/v1674813410/landingpage/TutorProfile.png"
                              />
                            </Box>
                          </Stack>
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Stack>
                  <Field
                    name="tutor_description"
                    render={({ field, form }: any) => {
                      console.log("field", field);
                      return (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <ExpandingTextArea
                            {...field}
                            {...commonProps(field, form)}
                            defaultValue={field.value}
                            name={field.name}
                            streamingLoading={streamingCall.loading}
                            streamingText={streamingCall.text}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                              tutor_description_storage.setValue(value);
                            }}
                          />
                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  />

                  <Box pb={5} pt={8}>
                    <Text fontWeight="semibold" fontSize="2xl">
                      Proudest accomplishment
                    </Text>
                    <Text color="gray.600">
                      Tell us what you consider to be your most outstanding
                      accomplishment as a person or as a teacher. Feel free to
                      blow your trumpet. Highlight your proudest achievements,
                      results or feats.
                    </Text>
                  </Box>

                  <Field
                    name="trackRecords"
                    render={({ field, form }: any) => (
                      <FormControl
                        isInvalid={commonProps(field, form).isInvalid}
                      >
                        <ExpandingTextArea
                          {...field}
                          {...commonProps(field, form)}
                          name={field.name}
                          rows={3}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                            trackRecords_storage.setValue(value);
                          }}
                        />
                        <FormErrorMessage>
                          {commonProps(field, form).errorMessage}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  />
                </Stack>

                <SingleFormButton
                  nextText="Save description"
                  isLoading={loading}
                  onNextClick={handleSubmit}
                />
              </form>
            </Box>
          );
        }}
      />
    </div>
  );
};

const TutorProfileEditForms: React.FC<{
  currentForm: string;
  onBackClick: () => void;
  children: any;
  onSubmit: (params: any, bool?: boolean) => Promise<any>;
  generateProfileSummary?: () => any;
  tutor_profile: {
    academic_preferences: any;
    language_preferences: any;
    test_prep_preferences: any;
    music_preferences: any;
    tutor_description: string;
    trackRecords: string;
    special_needs: any;
  };
  isLiteTutor?: boolean;
}> = observer(
  ({
    currentForm,
    onBackClick,
    onSubmit,
    tutor_profile,
    children,
    isLiteTutor,
    generateProfileSummary,
  }) => {
    const [loading, setLoading] = React.useState(false);
    const onFormSubmit = async (values: any) => {
      try {
        setLoading(true);
        await onSubmit(values);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        throw error;
      }
    };
    function getFormRender() {
      window.scrollTo(0, 0);
      if (currentForm === PROFILE_STEPS.ACADEMICS) {
        return (
          <AcademicPreference
            fields={TUTOR_PROFILE_PREFERENCES("Academics", isLiteTutor) as any}
            disabledOptions={
              isLiteTutor
                ? ["Junior Secondary", "Lower Primary", "Upper Primary"]
                : []
            }
            onSubmit={onFormSubmit}
            loading={loading}
            values={tutor_profile.academic_preferences}
            onBackClick={onBackClick}
          />
        );
      }
      if (currentForm === PROFILE_STEPS.MUSIC) {
        return (
          <AcademicPreference
            fields={TUTOR_PROFILE_PREFERENCES("Music") as any}
            onSubmit={onFormSubmit}
            loading={loading}
            values={tutor_profile.music_preferences}
            onBackClick={onBackClick}
          />
        );
      }
      if (currentForm === PROFILE_STEPS.LANGUAGES) {
        return (
          <AcademicPreference
            fields={TUTOR_PROFILE_PREFERENCES("Languages") as any}
            onSubmit={onFormSubmit}
            loading={loading}
            values={tutor_profile.language_preferences}
            onBackClick={onBackClick}
          />
        );
      }

      if (currentForm === PROFILE_STEPS.PROFILE_DESCRIPTION) {
        return (
          <ProfileDescriptionForm
            loading={loading}
            value={{
              tutor_description: tutor_profile.tutor_description ?? "",
              trackRecords: tutor_profile.trackRecords ?? "",
            }}
            onSubmit={onFormSubmit}
            onBackClick={onBackClick}
            generateProfileSummary={generateProfileSummary}
          />
        );
      }

      if (currentForm === PROFILE_STEPS.SPECIAL_NEEDS) {
        return (
          <div>
            <Button
              mb={6}
              onClick={onBackClick}
              leftIcon={<ArrowBackIcon />}
              variant="link"
              colorScheme="blue"
            >
              Back
            </Button>
            <SpecialNeeds
              onSubmit={onFormSubmit}
              loading={loading}
              specialNeeds={tutor_profile.special_needs || {}}
            />
          </div>
        );
      }

      return null;
    }

    return (
      <Box>
        {getFormRender()}
        {children}
      </Box>
    );
  }
);

const TutorProfileView: React.FC<{
  children: (currentForm: string, onBackClick: any) => React.ReactNode;
  Component: any;
  onSubjectEdit?: any;
  canSubmitSubject?: boolean;
  navigateToSubject?: any;
  tutor_profile: {
    tutor_description: string;
    trackRecords: string;
    academic_preferences: any;
    special_needs: any;
    music_preferences: any;
    language_preferences: any;
  };
  generateProfileSummary?: () => Promise<any>;
  isLiteTutor?: boolean;
  toggleNavigation?: (bool: boolean) => void;
  loading?: boolean;
  displaySubmit?: boolean;
  onSubmitFormSection: (currentForm: string, params: any) => Promise<any>;
}> = observer(
  ({
    onSubjectEdit,
    children,
    Component,
    navigateToSubject,
    tutor_profile,
    onSubmitFormSection,
    generateProfileSummary,
    toggleNavigation,
    displaySubmit = true,
    loading,
    isLiteTutor,
    ...rest
  }) => {
    const [currentForm, setCurrentForm] = React.useState("");
    const [clickedSubmit, setClickedSubmit] = React.useState(false);
    const [toggled, setToggled] = React.useState(false);
    const [localProfile, setLocalProfile] = React.useState(tutor_profile);
    const [invalidForm, setInValidForm] = React.useState({
      tutor_description: false,
      academic_preferences: false,
    });
    const canSubmit = Boolean(
      localProfile.tutor_description &&
        localProfile.academic_preferences?.classes?.length
    );
    const handleSubmit = () => {
      if (canSubmit) {
        setClickedSubmit(false);
        setInValidForm(() => ({
          tutor_description: false,
          academic_preferences: false,
        }));
        navigateToSubject(localProfile);
      } else {
        window.scrollTo(0, 0);
        setInValidForm(() => ({
          tutor_description: !Boolean(localProfile.tutor_description),
          academic_preferences: !Boolean(
            localProfile.academic_preferences?.classes?.length
          ),
        }));
        setClickedSubmit(true);
      }
    };

    const updateTutorProfile = (values: any) => {
      setLocalProfile({
        ...localProfile,
        ...values,
      });
    };

    React.useEffect(() => {
      setInValidForm(() => ({
        tutor_description: !Boolean(localProfile.tutor_description),
        academic_preferences: !Boolean(
          localProfile.academic_preferences?.classes?.length
        ),
      }));
    }, [localProfile.tutor_description, localProfile.academic_preferences]);

    return (
      <Component
        path="/tutor-profile"
        onSubjectEdit={onSubjectEdit}
        setCurrentForm={setCurrentForm}
        render={({ onBackClick }: any) => {
          const onNewBackClick = () => {
            onBackClick();
            toggleNavigation && toggleNavigation(true);
          };
          // if (!currentForm && !toggled) {
          //   setToggled(true);
          //   if (window.location.hash) {
          //     window.location.hash = "";
          //   }
          //   return null;
          // }
          return (
            <TutorProfileEditForms
              isLiteTutor={isLiteTutor}
              generateProfileSummary={generateProfileSummary}
              onBackClick={onNewBackClick}
              tutor_profile={localProfile as any}
              onSubmit={async (values, back = true) => {
                try {
                  // await subjectStore.checkSpellingAndSimilarity(
                  //   values,
                  //   currentForm
                  // );
                  if (currentForm === PROFILE_STEPS.ACADEMICS) {
                    updateTutorProfile({ academic_preferences: values });
                  }
                  if (currentForm === PROFILE_STEPS.TEST_PREP) {
                    updateTutorProfile({ test_prep_preferences: values });
                  }
                  if (currentForm === PROFILE_STEPS.MUSIC) {
                    updateTutorProfile({ music_preferences: values });
                  }
                  if (currentForm === PROFILE_STEPS.LANGUAGES) {
                    updateTutorProfile({ language_preferences: values });
                  }
                  if (currentForm === PROFILE_STEPS.PROFILE_DESCRIPTION) {
                    updateTutorProfile({
                      tutor_description: values.tutor_description,
                      trackRecords: values.trackRecords,
                    });
                  }
                  if (currentForm === PROFILE_STEPS.SPECIAL_NEEDS) {
                    updateTutorProfile({ special_needs: values });
                  }
                  const shortKeys = {
                    [PROFILE_STEPS.ACADEMICS]: "academic_preferences",
                    [PROFILE_STEPS.TEST_PREP]: "test_prep_preferences",
                    [PROFILE_STEPS.MUSIC]: "music_preferences",
                    [PROFILE_STEPS.LANGUAGES]: "language_preferences",
                    [PROFILE_STEPS.PROFILE_DESCRIPTION]: "tutor_description",
                    [PROFILE_STEPS.SPECIAL_NEEDS]: "special_needs",
                  };
                  await onSubmitFormSection(shortKeys[currentForm], values);
                  if (back) {
                    onBackClick();
                  }
                } catch (error) {
                  console.log(error);
                  displayToast({
                    status: "error",
                    title: "",
                    duration: 4000,
                    position: "top",
                    description: "Oops! There was a problem. Please try again",
                  });
                  throw error;
                }
              }}
              currentForm={currentForm}
              {...rest}
            >
              {children(currentForm, onBackClick)}
            </TutorProfileEditForms>
          );
        }}
      >
        {({ onMenuClick: defaultMenuClick }: any) => {
          let onMenuClick = (key: any) => {
            setClickedSubmit(false);
            defaultMenuClick(key);
            toggleNavigation && toggleNavigation(false);
          };
          return (
            <Box as="main">
              <Stack
                spacing={10}
                id="form-header-section"
                className="tutoring-profile-form"
              >
                <Stack spacing={4} mb={1}>
                  <Text
                    fontWeight="bold"
                    lineHeight="shorter"
                    fontSize={["3xl", "4xl"]}
                  >
                    Tutoring profile
                  </Text>
                  <Text color="gray.700">
                    Complete the following sections to tell us, and potential
                    clients, about your tutoring experience.
                  </Text>
                </Stack>

                <Stack spacing={4}>
                  <Stack spacing={1}>
                    <Text
                      fontSize={{ base: "xl", md: "2xl" }}
                      fontWeight="bold"
                    >
                      Tutoring categories
                    </Text>
                    <Text color="gray.700">
                      Complete the relevant section below only if you intend to
                      teach subjects in Academics, Music or Languages.
                    </Text>
                  </Stack>
                  <Stack spacing={3}>
                    <MobileMenuItem
                      isPopulated={
                        localProfile.academic_preferences &&
                        Object.keys(localProfile.academic_preferences).length >
                          0
                      }
                      itemName={PROFILE_STEPS.ACADEMICS}
                      placeholder="Like Math, English, Physics etc."
                      errorMessage="Please specify class group and curriculum"
                      itemDescription={""}
                      isInvalid={invalidForm.academic_preferences}
                      onClick={() => onMenuClick(PROFILE_STEPS.ACADEMICS)}
                    />
                    {!isLiteTutor && (
                      <>
                        <MobileMenuItem
                          isPopulated={
                            localProfile?.music_preferences &&
                            Object.keys(localProfile?.music_preferences)
                              .length > 0
                          }
                          itemName={PROFILE_STEPS.MUSIC}
                          placeholder="Like Piano, Guitar, Music Theory etc."
                          errorMessage="Please enter a description"
                          itemDescription={""}
                          isInvalid={false}
                          onClick={() => onMenuClick(PROFILE_STEPS.MUSIC)}
                        />
                        <MobileMenuItem
                          itemName={PROFILE_STEPS.LANGUAGES}
                          placeholder="Like Nigerian, African or Other Languages"
                          errorMessage="Please enter a description"
                          itemDescription={""}
                          isInvalid={false}
                          isPopulated={
                            localProfile?.language_preferences &&
                            Object.keys(localProfile?.language_preferences)
                              .length > 0
                          }
                          onClick={() => onMenuClick(PROFILE_STEPS.LANGUAGES)}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>

                <Stack>
                  <Stack spacing={1}>
                    <Text
                      fontSize={{ base: "xl", md: "2xl" }}
                      fontWeight="bold"
                    >
                      Special needs
                    </Text>
                    <Text color="gray.700">
                      Complete this section if you have experience with special
                      needs learners.
                    </Text>
                  </Stack>
                  <Stack spacing={3}>
                    <MobileMenuItem
                      itemName={PROFILE_STEPS.SPECIAL_NEEDS}
                      placeholder="Tell us your qualification"
                      itemDescription={
                        localProfile.special_needs?.specialNeedsExperience ?? ""
                      }
                      onClick={() => onMenuClick(PROFILE_STEPS.SPECIAL_NEEDS)}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={4}>
                  <Stack spacing={1}>
                    <Text
                      fontSize={{ base: "xl", md: "2xl" }}
                      fontWeight="bold"
                    >
                      Profile summary
                    </Text>
                    <Text color="gray.700">
                      Your profile summary is like your bio, it's how you
                      introduce yourself to the entire Tuteria community.
                    </Text>
                  </Stack>
                  <MobileMenuItem
                    isPopulated={Boolean(localProfile.tutor_description)}
                    itemName={PROFILE_STEPS.PROFILE_DESCRIPTION}
                    placeholder="Write a compelling profile"
                    errorMessage="Please enter a profile summary"
                    isInvalid={invalidForm.tutor_description}
                    itemDescription={localProfile.tutor_description}
                    onClick={() =>
                      onMenuClick(PROFILE_STEPS.PROFILE_DESCRIPTION)
                    }
                  />
                </Stack>

                {displaySubmit && (
                  <Box width="full" pt={5}>
                    <Button
                      isFullWidth
                      size="lg"
                      isLoading={loading}
                      loadingText="Saving Profile"
                      onClick={handleSubmit}
                      colorScheme="blue"
                    >
                      Save and submit
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          );
        }}
      </Component>
    );
  }
);

const OverlayWrapperComponent = ({ children, ...rest }: any) => {
  return (
    <OverlayRouter>
      <OverlayWrapper {...rest}>{children}</OverlayWrapper>
    </OverlayRouter>
  );
};

export const TutorProfilePage: React.FC<{
  type?: "modal" | "hide";
  onSubjectEdit?: any;
  navigateToSubject?: any;
  tutor_profile: any;
  children?: any;
  onSubmitFormSection: any;
  getProfileSummary: () => Promise<any>;
  displaySubmit?: boolean;
  loading?: boolean;
  isLiteTutor?: boolean;
  toggleNavigation?: (value: boolean) => void;
}> = observer(
  ({
    onSubjectEdit,
    children,
    type = "modal",
    navigateToSubject,
    displaySubmit = true,
    tutor_profile,
    onSubmitFormSection,
    ...rest
  }) => {
    // const Wrapper = type === "modal" ? OverlayWrapper : React.Fragment;
    const Wrapper = type === "modal" ? OverlayWrapperComponent : React.Fragment;
    const Component = type === "modal" ? ModalWrapper : HideAndShowWrapper;
    return (
      <Wrapper>
        <TutorProfileView
          displaySubmit={displaySubmit}
          navigateToSubject={navigateToSubject}
          onSubjectEdit={onSubjectEdit}
          Component={Component}
          tutor_profile={tutor_profile}
          onSubmitFormSection={onSubmitFormSection}
          {...rest}
        >
          {children}
        </TutorProfileView>
      </Wrapper>
    );
  }
);
